/*
  <store-selector> — modal list of physical stores.

  Opened by any element carrying [data-store-locator] (the header's "Our
  Physical Stores" link already does), so the trigger lives wherever the
  merchant put it rather than being owned by this component.

  The chosen store is kept in localStorage so the selection survives
  navigation — the customer states a preference once rather than on every
  page. Nothing here talks to checkout; this is presentational.
*/

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

const STORAGE_KEY = 'neroli:selected-store';

class StoreSelector extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('.store-selector__dialog');
    this.radios = Array.from(this.querySelectorAll('[data-store-radio]'));
    this.mediaImage = this.querySelector('[data-store-media-image]');
    if (!this.dialog || this.radios.length === 0) return;

    this.open = this.open.bind(this);
    this.close = this.close.bind(this);
    this.onKeydown = this.onKeydown.bind(this);

    // Delegated rather than bound per element: the drawer is portalled by
    // header.js, so its trigger may not exist — or may have moved — by the
    // time this runs. Delegation sidesteps that ordering entirely.
    this.onDocumentClick = this.onDocumentClick.bind(this);
    document.addEventListener('click', this.onDocumentClick);

    this.querySelectorAll('[data-store-close]').forEach((el) => {
      el.addEventListener('click', this.close);
    });

    this.querySelectorAll('[data-store-expand]').forEach((button) => {
      button.addEventListener('click', () => this.toggleDetails(button));
    });

    // The chevron alone was too small a target and read as the only way in.
    // Clicking anywhere on the row now both selects that store and opens its
    // details, which is what "I'm looking at this one" means in practice.
    // Only one store's details stay open at a time, so the list never grows
    // unmanageably long.
    this.querySelectorAll('[data-store-item]').forEach((item) => {
      const row = item.querySelector('.store-selector__row');
      row?.addEventListener('click', (event) => {
        // Let the chevron and the radio keep their own behaviour.
        if (event.target.closest('[data-store-expand]')) return;

        const radio = item.querySelector('[data-store-radio]');
        if (radio && !radio.checked) {
          radio.checked = true;
          radio.dispatchEvent(new Event('change', { bubbles: true }));
        }

        const button = item.querySelector('[data-store-expand]');
        if (button && button.getAttribute('aria-expanded') !== 'true') {
          this.toggleDetails(button);
        }
      });
    });

    this.radios.forEach((radio) => {
      radio.addEventListener('change', () => this.onSelect(radio));
    });

    this.confirm = this.querySelector('[data-store-confirm]');
    this.confirm?.addEventListener('click', (event) => {
      this.persist();

      // As a link with a destination, let the browser navigate — closing the
      // modal first would be pointless. Block the click only when it rendered
      // as a link but this store has nowhere to go.
      if (this.confirm.tagName === 'A') {
        if (!this.confirm.getAttribute('href')) event.preventDefault();
        return;
      }
      this.close();
    });

    this.restore();
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onDocumentClick);
    document.removeEventListener('keydown', this.onKeydown);
    document.body.style.overflow = '';
  }

  /* ---------- open / close ---------- */

  onDocumentClick(event) {
    const trigger = event.target.closest('[data-store-locator]');
    if (!trigger) return;
    // The trigger is an <a href> so it still works without JS; intercept it
    // here so the modal opens in place instead of navigating.
    event.preventDefault();
    // The drawer carries its own trigger. Leaving it open would stack two
    // overlays and trap focus in the wrong one, so it is dismissed first.
    const drawer = document.querySelector('mobile-menu:not([hidden])');
    if (drawer && typeof drawer.close === 'function') drawer.close();
    this.lastTrigger = trigger;
    this.open();
  }

  get triggers() {
    return Array.from(document.querySelectorAll('[data-store-locator]'));
  }

  get isOpen() {
    return !this.hidden;
  }

  open() {
    this.hidden = false;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', this.onKeydown);
    this.triggers.forEach((trigger) => trigger.setAttribute('aria-expanded', 'true'));

    // Prefer the already-selected store so returning customers land on their
    // own choice rather than the top of the list.
    const checked = this.radios.find((radio) => radio.checked);
    (checked || this.radios[0])?.focus();
  }

  close() {
    if (!this.isOpen) return;
    this.hidden = true;
    document.body.style.overflow = '';
    document.removeEventListener('keydown', this.onKeydown);
    this.triggers.forEach((trigger) => trigger.setAttribute('aria-expanded', 'false'));
    // Focus returns to whichever control opened the modal, not always the
    // header one — the drawer has its own trigger.
    (this.lastTrigger || this.triggers[0])?.focus();
  }

  onKeydown(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      return;
    }
    if (event.key !== 'Tab') return;

    // Recomputed per Tab because expanding a store changes what is focusable.
    const focusable = Array.from(this.dialog.querySelectorAll(FOCUSABLE)).filter(
      (el) => el.offsetParent !== null
    );
    if (focusable.length === 0) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  /* ---------- details ---------- */

  toggleDetails(button) {
    const panel = this.querySelector(`#${CSS.escape(button.getAttribute('aria-controls'))}`);
    if (!panel) return;
    const isOpen = button.getAttribute('aria-expanded') === 'true';

    // Accordion: one open at a time. With several stores expanded the list
    // became long enough that the remaining choices scrolled out of reach,
    // which is the problem this solves.
    if (!isOpen) {
      this.querySelectorAll('[data-store-expand][aria-expanded="true"]').forEach((other) => {
        if (other === button) return;
        const otherPanel = this.querySelector(`#${CSS.escape(other.getAttribute('aria-controls'))}`);
        other.setAttribute('aria-expanded', 'false');
        if (otherPanel) otherPanel.hidden = true;
      });
    }

    button.setAttribute('aria-expanded', isOpen ? 'false' : 'true');
    panel.hidden = isOpen;

    // Opening a store lower down would otherwise leave its content below the
    // fold with no indication it had expanded.
    if (!isOpen) {
      const item = button.closest('[data-store-item]');
      const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      requestAnimationFrame(() => {
        item?.scrollIntoView({ block: 'nearest', behavior: reduced ? 'auto' : 'smooth' });
      });
    }
  }

  /* ---------- selection ---------- */

  onSelect(radio) {
    // Swap the side image to match the highlighted store, so the panel is
    // about the store being considered rather than a fixed decoration.
    const src = radio.getAttribute('data-store-image');
    if (src && this.mediaImage) {
      this.mediaImage.src = src;
      this.mediaImage.removeAttribute('srcset');
    }

    // The button follows the selection: a store's own link wins, otherwise
    // the section-level fallback. Only applies when the control rendered as
    // an <a>; with no links configured it stays a plain confirm button.
    if (this.confirm && this.confirm.tagName === 'A') {
      const storeLink = radio.getAttribute('data-store-link');
      const fallback = this.confirm.getAttribute('data-store-fallback-link');
      const href = storeLink || fallback;
      if (href) {
        this.confirm.href = href;
        this.confirm.removeAttribute('aria-disabled');
      } else {
        // Nothing to navigate to for this store — keep it inert rather than
        // sending the customer to "#".
        this.confirm.removeAttribute('href');
        this.confirm.setAttribute('aria-disabled', 'true');
      }
    }
  }

  persist() {
    const checked = this.radios.find((radio) => radio.checked);
    if (!checked) return;
    try {
      localStorage.setItem(STORAGE_KEY, checked.value);
    } catch (error) {
      // Private browsing and storage-disabled contexts: the selection simply
      // doesn't persist, which is not worth breaking the modal over.
    }
  }

  restore() {
    let saved = null;
    try {
      saved = localStorage.getItem(STORAGE_KEY);
    } catch (error) {
      return;
    }
    if (!saved) return;

    const match = this.radios.find((radio) => radio.value === saved);
    if (match) {
      match.checked = true;
      this.onSelect(match);
    }
  }
}

customElements.define('store-selector', StoreSelector);
